import type { ButtonHTMLAttributes, Ref } from "preact";
import { forwardRef } from "preact/compat";
import { classNames } from "@gss/common/classNames";
import type { Variant } from "@gss/types/Variant";
import "./Button.css";

export type ButtonVariant = Variant | "link" | "default" | `outline-${Variant}`;

interface Props extends ButtonHTMLAttributes {
    variant: ButtonVariant | null;
    small?: boolean;
    className?: string;
}

export const Button = forwardRef(
    (
        { variant, small, className, children, ...rest }: Props,
        ref: Ref<HTMLButtonElement>,
    ) => {
        return (
            <button
                ref={ref}
                type="button"
                className={classNames(
                    "btn",
                    variant != null && getVariantClass(variant),
                    small && "btn-sm",
                    className,
                )}
                {...rest}
            >
                {children}
            </button>
        );
    },
);

Button.displayName = "Button";

function getVariantClass(variant: ButtonVariant): string {
    switch (variant) {
        case "primary":
            return "btn-primary";
        case "secondary":
            return "btn-secondary";
        case "success":
            return "btn-success";
        case "danger":
            return "btn-danger";
        case "warning":
            return "btn-warning";
        case "info":
            return "btn-info";
        case "light":
            return "btn-light";
        case "dark":
            return "btn-dark";

        case "link":
            return "btn-link";
        case "default":
            return "btn-default";

        case "outline-primary":
            return "btn-outline-primary";
        case "outline-secondary":
            return "btn-outline-secondary";
        case "outline-success":
            return "btn-outline-success";
        case "outline-danger":
            return "btn-outline-danger";
        case "outline-warning":
            return "btn-outline-warning";
        case "outline-info":
            return "btn-outline-info";
        case "outline-light":
            return "btn-outline-light";
        case "outline-dark":
            return "btn-outline-dark";

        default: {
            const _ensureExhaustive: never = variant;
            throw new Error("Unknown Button variant");
        }
    }
}
